import {useCallback, useMemo, useState} from 'react';
import debounce from 'lodash/debounce';
import type {HttpError} from 'api/http/types';
import type {Column, DataResponse, Filter, ViewMode} from 'api/types';
import {useLocalization} from 'localization';
import {useSnacks} from 'components';
import type {DataFetchType} from '../types';
import {getExportFilename} from '../functions/getExportFilename';
import {useSearcher} from './useSearcher';

export const useDataFetch = (allColumns: Column[]): DataFetchType => {
  const {errorSnack} = useSnacks();
  const {t} = useLocalization();
  const {search, download} = useSearcher(allColumns);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [dataResponse, setDataResponse] = useState<DataResponse>({
    rows: [],
    hits: 0,
  });

  const searchHandler = useCallback(
    (
      type: ViewMode,
      filters: Filter[],
      columns?: string[],
      sort?: string,
      page?: number,
      size?: number,
    ) => {
      setLoadingSearch(true);

      search(type, filters, columns, page, size, sort)
        .then(response => {
          setDataResponse(response);
        })
        .catch(error => errorSnack(t('data.searching.failed'), error))
        .finally(() => {
          setLoadingSearch(false);
        });
    },
    [errorSnack, search, t],
  );

  const debouncedSearchHandler = useMemo(
    () => debounce(searchHandler, 200),
    [searchHandler],
  );

  const triggerDownload = useCallback((data: Blob) => {
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = getExportFilename('xlsx');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleDownload = useCallback(
    async (
      type: ViewMode,
      filters: Filter[],
      columns?: string[],
      distinctOn?: string,
    ) => {
      setLoadingDownload(true);

      try {
        try {
          const data = await download(type, filters, columns, distinctOn);
          return triggerDownload(data);
        } catch (error) {
          return errorSnack(t('data.download.failed'), error as HttpError);
        }
      } finally {
        setLoadingDownload(false);
      }
    },
    [download, errorSnack, t, triggerDownload],
  );

  return useMemo(
    () => ({
      dataResponse,
      loadingSearch,
      onSearch: debouncedSearchHandler,
      loadingDownload,
      onDownload: handleDownload,
    }),
    [
      dataResponse,
      debouncedSearchHandler,
      handleDownload,
      loadingDownload,
      loadingSearch,
    ],
  );
};
